/**
 * TimeSlots Types
 *
 * TypeScript type definitions for the timeslot selection UI component.
 * These types define the core data structures for timeslot display,
 * selection state management, and time period grouping.
 *
 * @requirements 1.3, 1.4, 1.5 - Core Timeslot Display status types
 */

/**
 * Slot status representing the current state of a timeslot.
 * - 'open': Available for booking, displayed with green accent (#33B897)
 * - 'blocked': Time-off period set by doctor, displayed with grey (#A4A4A4), cannot be selected
 * - 'booked': Has a scheduled appointment, displayed with red (#F43E51) or appointment status color
 */
export type SlotStatus = 'open' | 'blocked' | 'booked'

/**
 * Represents an individual timeslot in the scheduling grid.
 * Each slot represents a 10-minute scheduling interval.
 */
export interface TimeSlot {
  /** Unique identifier for the timeslot */
  id: string
  /** Slot start time */
  time: Date
  /** Duration in milliseconds (default 10 minutes = 600000ms) */
  duration: number
  /** Current status of the slot */
  status: SlotStatus
  /** Doctor ID - present if blocked (time-off) */
  doctorId?: string
  /** Patient ID - present if booked (appointment) */
  patientId?: string
  /** Reference to appointment if booked */
  appointmentId?: string
  /** Display information for booked slots */
  formattedStatus?: {
    /** Color to display for the slot */
    color: string
    /** Label to display for the slot status */
    label: string
  }
}

/**
 * Represents the current selection state for the timeslot grid.
 * Uses Set<string> for efficient O(1) lookup of selected/highlighted slots.
 */
export interface SelectionState {
  /** Currently selected slot IDs */
  selectedSlotIds: Set<string>
  /** Slots highlighted during drag operation */
  highlightedSlotIds: Set<string>
}

/**
 * Represents a time period grouping for the timeslot grid.
 * Timeslots are grouped into Morning, Afternoon, and Evening sections.
 */
export interface TimePeriod {
  /** Unique identifier for the time period */
  id: 'morning' | 'afternoon' | 'evening'
  /** Translation key for the period title */
  titleKey: string
  /** Translation key for the time range display */
  timeRangeKey: string
  /** Start hour of the period (inclusive, 0-23) */
  startHour: number
  /** End hour of the period (exclusive, 0-24) */
  endHour: number
}

/**
 * Time period constants for grouping timeslots.
 * - Morning: 00:00 - 12:00 (hours 0-11)
 * - Afternoon: 12:00 - 18:00 (hours 12-17)
 * - Evening: 18:00 - 24:00 (hours 18-23)
 */
export const TIME_PERIODS: TimePeriod[] = [
  {
    id: 'morning',
    titleKey: 'mornings',
    timeRangeKey: 'morningTimeRange',
    startHour: 0,
    endHour: 12,
  },
  {
    id: 'afternoon',
    titleKey: 'afternoons',
    timeRangeKey: 'afternoonTimeRange',
    startHour: 12,
    endHour: 18,
  },
  {
    id: 'evening',
    titleKey: 'evenings',
    timeRangeKey: 'eveningTimeRange',
    startHour: 18,
    endHour: 24,
  },
]
